import { buildSingleNeedleRegex, normalizeForSearch } from "../documentSearch";
import { findPrecedingSectionHeading } from "./sectionHeuristics";

// Long enough to be unique in a document, short enough that OCR drift further into the selection
// cannot break the match. Mirrors the anchor length the citation matcher settled on.
const NEEDLE_LENGTH = 80;
const MIN_NEEDLE_LENGTH = 4;

/**
 * The heading text a markdown line shows, with the syntax the OCR wrote around it taken off: link
 * wrappers, emphasis and code markers, and the backslash escapes Mistral puts in front of punctuation
 * (`Fees \& Charges`). Without this the section reads as raw markdown while the clause beside it, taken
 * from the browser selection, reads as the document does.
 *
 * Markers are dropped before escapes are resolved, and the lookbehind keeps an escaped marker out of
 * that pass so `\*` survives to become the literal asterisk the document actually shows.
 */
export function stripMarkdownFormatting(line: string): string {
    return line
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/(?<!\\)(\*\*|\*|~~|`)/g, "")
        .replace(/\\([!-/:-@[-`{-~])/g, "$1")
        .replace(/[ \t]{2,}/g, " ")
        .trim();
}

/**
 * The character offset each page starts at, in page order, as the search index stores it:
 * `pageOffsets[n - 1]` is where page n begins.
 */
function getPageBounds(content: string, pageOffsets: Array<number>, pageNumber: number | undefined) {
    const wholeDocument = { text: content, offset: 0 };

    if (!pageNumber || pageOffsets.length === 0) {
        return wholeDocument;
    }

    const start = pageOffsets[pageNumber - 1];
    if (start === undefined) {
        return wholeDocument;
    }

    const end = pageOffsets[pageNumber] ?? content.length;
    return { text: content.substring(start, end), offset: start };
}

/**
 * The section a selected clause sits in, read from the document's extracted markdown rather than
 * guessed from how the document happens to be rendered. Mistral's OCR already marked the headings, so
 * the section is whatever `#` heading precedes the clause.
 *
 * The clause is looked for on its own page first, so a phrase repeated elsewhere in the document cannot
 * pull the answer to the wrong place, then across the whole document as a fallback. The heading is then
 * looked for from that position backwards through the *whole* document, so a section continuing across
 * a page break still resolves to the heading that opened it.
 *
 * Returns undefined when the clause cannot be found, which leaves the user to name the section.
 */
export function resolveSectionFromMarkdown(content: string, pageOffsets: Array<number>, clause: string, pageNumber: number | undefined): string | undefined {
    const needle = clause.trim().substring(0, NEEDLE_LENGTH);
    if (needle.length < MIN_NEEDLE_LENGTH) {
        return undefined;
    }

    const page = getPageBounds(content, pageOffsets, pageNumber);
    const onPage = buildSingleNeedleRegex(needle).exec(normalizeForSearch(page.text));
    const match = onPage ? { index: page.offset + onPage.index, length: onPage[0].length } : findInWholeDocument(content, needle);

    if (!match) {
        return undefined;
    }

    // normalizeForSearch preserves character count, so a match offset in the normalised text is also
    // the offset in the original.
    const upToClause = content.substring(0, match.index + match.length);
    // Cleaned before classifying rather than after: the `#` prefix survives so the heading rule still
    // fires, and the length thresholds then measure the heading itself instead of one padded with
    // markers that the trim would otherwise cut through.
    const lines = upToClause.split("\n").map(stripMarkdownFormatting);

    return findPrecedingSectionHeading(lines, lines.length - 1);
}

function findInWholeDocument(content: string, needle: string) {
    const match = buildSingleNeedleRegex(needle).exec(normalizeForSearch(content));
    return match ? { index: match.index, length: match[0].length } : undefined;
}
